// Expanding ring flash for abilities like Garen's Judgment.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import { TIMING } from "../../config/constants";
import type { Point } from "../../engine/hex/HexLayout";

export class AoERingVFX implements Animation {
  private elapsed = 0;
  private duration = TIMING.abilityWindup + TIMING.abilityActive;

  constructor(
    private center: Point,
    private maxRadius: number,
    private color: string = "#f0e6d2",
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, this.duration);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const progress = this.elapsed / this.duration;

    if (progress < TIMING.abilityWindup / this.duration) {
      // Windup: charge-up circle pulses at center.
      const p = progress / (TIMING.abilityWindup / this.duration);
      const r = 10 + Easing.easeOutQuad(p) * 20;
      ctx.save();
      ctx.globalAlpha = 0.5 + p * 0.5;
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(this.center.x, this.center.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    // Active: ring expands to maxRadius and fades.
    const activeP =
      (progress - TIMING.abilityWindup / this.duration) /
      (TIMING.abilityActive / this.duration);
    const eased = Easing.easeOutCubic(activeP);
    const radius = this.maxRadius * eased;
    const alpha = (1 - activeP) * 0.85;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 6 * (1 - activeP) + 2;
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 20;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, radius, 0, Math.PI * 2);
    ctx.stroke();

    // Inner glow
    ctx.globalAlpha = alpha * 0.3;
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, radius * 0.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= this.duration;
  }
}
